/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import XRegExp from 'xregexp';

/**
 * This module contains reusable regular expressions.
 *
 * NOTE: this module needs to exist both here and in the server component,
 * so any changes made here MUST ALSO be made to:
 *
 * packages/server-ts/src/helpers/regex.ts
 */

/**
 * Enumeration containing the regular expressions as strings, so they may
 * be combined with other regular expressions as needed.
 * Note that they make use of comments and liberal spacing, so they must
 * be used within the XRegExp constructor using the "x" flag to allow for
 * the comments.
 */
export const enum RegularExpression {
  // This regex is based off of https://gist.github.com/dperini/729294
  // but with some modifications.
  SERVER_URL = `
    # Protocol: accepts "http" and "https"
    (?:(?:https?:)\/\/)
    # Optional user and password (separated by a ":"), followed by a "@"
    (?:[^\\s:]+(?::[^\\s:]*)?@)?
    # Now we have an option: either an IPv4 address or a hostname
    (?:
      (?:
          # First, a sequence of 3 numbers (0-255) followed by a dot
          (?:(?:[1-9]?\\d|1\\d\\d|25[0-5]|2[0-4]\\d)\.){3}
          # Finally, another number (0-255)
          [1-9]?\\d|1\\d\\d|25[0-5]|2[0-4]\\d
      )
    |
      (?:
        # The first section must contain only letters, digits or hyphens.
        # We use forward lookahead with back-references (to simulate atomic
        # groups) in order to prevent backtracking: once a group has matched,
        # the engine will not attempt to backtrack into those matches, thus
        # preventing catastrophic backtracking.
        (?=([a-z\\u00a1-\\uffff0-9-]+))\\1
        # Followed by 0+ dot-separated sections using the same rule as above.
        (?=
          ((?:\\.
            (?=([a-z\\u00a1-\\uffff0-9-]+))\\3*
          )*)
        )\\2
        # The hostname may optionally end in a dot
        \\.?
      )
    )
    # May be followed by a port (a colon followed by 2-5 digits)
    (?::\\d{2,5})?
    # May be followed by a path, which may contain different path elements
    (?:[\/?#]\\S*)?
  `
}

/**
 * A regular expression that matches a full URL (including a protocol of
 * "http" or "https", optional user/password, IPv4 or hostname, optional
 * port, and optional path elements). It contains non-capturing groups, so
 * it can only be used to validate a URL, not to extract any of its composing
 * elements. It is anchored both at the start and the end of the expression.
 */
export const SERVER_URL_REGEX = XRegExp(
  `^${RegularExpression.SERVER_URL}$`,
  'x'
);

/**
 * A regular expression that matches one or more non-whitespace characters
 * separated by ":", followed by whitespace, and then one or more
 * non-whitespace characters.
 */
export const TIME_TIMEZONE_REGEX = /(([^:\s]+):([^:\s]+))\s(\S+)/;
